/// <amd-module name="TFS/CreateProject/ActionCreator" />
import { ActionsHub, IStatusPayload } from "TFS/CreateProject/ActionsHub";
import { Constants } from "TFS/CreateProject/Constants";
import {
    ICollectionProjectNamesData,
    INewProjectParameters,
    IProjectCreationMetadata,
    StatusMessageType,
    StatusValueType,
    TfsError
} from "TFS/CreateProject/Contracts";
import { IStatusMessage } from "TFS/CreateProject/Models";
import { validateProjectName, ValidationResult } from "TFS/CreateProject/ProjectNameValidator";
import * as CreateProjectResources from "TFS/CreateProject/Resources";
import { ApiSource } from "TFS/CreateProject/Sources/ApiSource";
import { DataProviderSource } from "TFS/CreateProject/Sources/DataProviderSource";
import { IOperationResult, OperationResultSource } from "TFS/CreateProject/Sources/OperationResultSource";
import { ITfsProductFeaturesService } from "Tfs/Platform/ProductFeatures";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { OperationReference } from "VSS/Platform/Contracts/Operations";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export class ActionCreator {
    private projectLimit = 1000;
    private projectWarning = 250;
    private projectPerformanceWarningThreshold = 75;

    private get apiSource(): ApiSource {
        if (!this._apiSource) {
            this._apiSource = new ApiSource(this._pageContext);
        }
        return this._apiSource;
    }

    private get dataProviderSource(): DataProviderSource {
        if (!this._dataProviderSource) {
            this._dataProviderSource = new DataProviderSource(this._pageContext);
        }
        return this._dataProviderSource;
    }

    private get operationResultSource(): OperationResultSource {
        if (!this._operationResultSource) {
            this._operationResultSource = new OperationResultSource(this._pageContext);
        }
        return this._operationResultSource;
    }
    private _isHosted: boolean;

    constructor(
        private _actionsHub: ActionsHub,
        private _pageContext: IVssPageContext,
        private _apiSource?: ApiSource,
        private _operationResultSource?: OperationResultSource,
        private _dataProviderSource?: DataProviderSource,
        private onCreateProjectComplete?: (newProject: string) => void
    ) {
        const pageData = this._pageContext.getService<IVssPageService>("IVssPageService").getData();
        this._isHosted = pageData ? pageData.isHosted : false;
    }

    /**
     * Fetches the initial data needed to host the project creation panel and invokes the initalization action appropriately
     * @param forceRequest - Optional flag. If true, a network call is made irrespective of data available in json island. Used for reload.
     */
    public fetchProjectCreationMetadata(forceRequest?: boolean): void {
        // To set inprogress state to the data load
        this._actionsHub.projectCreationMetadataLoadStarted.invoke({
            pageStatus: {
                value: StatusValueType.InProgress,
                message: ""
            },
            statusMessage: {
                message: "",
                statusMessageType: StatusMessageType.NoStatus,
                showGoBackCta: false,
                showTryAgainCta: false
            }
        });

        this.dataProviderSource.getInitialData(forceRequest).then((projectCreationMetadata: IProjectCreationMetadata | undefined) => {
            // Checking for required fields
            if (
                projectCreationMetadata &&
                (!projectCreationMetadata.canUserCreateProject ||
                    (projectCreationMetadata.canUserCreateProject &&
                        projectCreationMetadata.defaultVersionControlType &&
                        projectCreationMetadata.defaultProcessTemplate))
            ) {
                const pageStatus: IStatusPayload = {
                    value: StatusValueType.Success,
                    message: ""
                };
                const statusMessage: IStatusMessage = {
                    message: "",
                    statusMessageType: StatusMessageType.NoStatus,
                    showGoBackCta: false,
                    showTryAgainCta: false
                };

                if (!projectCreationMetadata.canUserCreateProject) {
                    statusMessage.statusMessageType = StatusMessageType.Warning;
                    statusMessage.showTryAgainCta = false;
                    statusMessage.message = pageStatus.message = CreateProjectResources.CreateProjectUnauthorizedAccessText;
                    pageStatus.value = StatusValueType.Failure;

                    // Announce create project access is not authorized.
                    announce(
                        Utils_String.format(
                            "{0} {1}",
                            CreateProjectResources.GenericErrorMessage,
                            CreateProjectResources.CreateProjectUnauthorizedAccessText
                        )
                    );
                }

                this._actionsHub.projectCreationMetadataLoadSucceeded.invoke({
                    projectCreationMetadata: projectCreationMetadata,
                    pageStatus: pageStatus,
                    statusMessage: statusMessage
                });
            } else {
                this._invokeProjectMetadataLoadFailedAction(new Error(CreateProjectResources.CreateProjectGenericServerErrorText));
            }
        }, this._invokeProjectMetadataLoadFailedAction);
    }

    /**
     * Fetches the existing project names required for project name validation
     */
    public fetchExistingProjectNames(): void {
        this.dataProviderSource.getExistingProjects().then((existingProjectData: ICollectionProjectNamesData | undefined) => {
            if (existingProjectData && existingProjectData["existingProjectNames"]) {
                const existingProjectNames = existingProjectData["existingProjectNames"];
                if (existingProjectNames.length > 0) {
                    this._actionsHub.existingProjectNamesFetched.invoke(existingProjectNames);
                }
            }
        });
    }

    /**
     * Update the project description in the store, with the new value
     * @param value - The new description value entered
     */
    public updateProjectDescription = (value: string): void => {
        this._actionsHub.projectDescriptionChanged.invoke(value);
    };

    /**
     * Update the version control type in the store based on the new selected index
     * @param selectedType - Id of the new version control type selected
     */
    public updateVersionControlType = (selectedType: string): void => {
        this._actionsHub.versionControlChanged.invoke(selectedType);
    };

    /**
     * Update the project visibility option in the store based on the new selected index
     * @param selectedIndex - Index of the new project visbility selected
     */
    public updateProjectVisibilityOption = (selectedIndex: number): void => {
        this._actionsHub.projectVisibilityChanged.invoke(selectedIndex);
    };

    /**
     * Update the process template in the store based on the new selected value
     * @param selectedValue - The value of the new process template selected
     */
    public updateProcessTemplate = (selectedValue: string): void => {
        this._actionsHub.processTemplateChanged.invoke(selectedValue);
    };

    /**
     * Reset the validation status for the project name textfield
     */
    public resetProjectNameValidation(): void {
        this._actionsHub.projectNameValidationReset.invoke({
            projectName: "",
            status: {
                value: StatusValueType.NoStatus,
                message: ""
            }
        });
    }

    /**
     * Checks the validity of the new project name
     * @param newProjectName - Name of the new project
     * @param existingProjectNames - Names of the existing projects in the collection
     */
    public validateAndUpdateProjectName(newProjectName: string, existingProjectNames: { [key: string]: boolean }): void {
        const validationResult = this._validateProjectName(newProjectName, existingProjectNames);
        if (validationResult.isValid) {
            // The name is valid, meets requirements, hence invoke the validation succeeded action
            this._actionsHub.projectNameValidationSucceeded.invoke({
                projectName: newProjectName,
                status: {
                    value: StatusValueType.Success,
                    message: ""
                }
            });
        } else if (newProjectName) {
            // Show error message only if project name is not empty
            this._actionsHub.projectNameValidationFailed.invoke({
                projectName: newProjectName,
                status: {
                    value: StatusValueType.Failure,
                    message: validationResult.invalidNameReason
                }
            });
        }
    }

    /**
     * Checks the number of existing projects against the allowed limit
     */
    public checkProjectLimit(): void {
        this.dataProviderSource.getExistingProjects().then((existingProjectData: ICollectionProjectNamesData | undefined) => {
            let warningCountToUse: number;
            let limitCountToUse: number;
            let performanceWarningToUse: number;

            const projectLimitResult = {
                isBelowLimit: false,
                isBelowWarning: false,
                errorReason: ""
            };

            if (existingProjectData) {
                // if registry value was read, use this instead of default value
                const projectLimitFromRegistry = existingProjectData.limit;
                if (projectLimitFromRegistry != null) {
                    limitCountToUse = projectLimitFromRegistry;
                } else {
                    limitCountToUse = this.projectLimit;
                }

                const projectWarningFromRegistry = existingProjectData.warning;
                if (projectWarningFromRegistry != null) {
                    warningCountToUse = projectWarningFromRegistry;
                } else {
                    warningCountToUse = this.projectWarning;
                }

                const performanceWarningFromRegistry = existingProjectData.performanceWarning;
                if (performanceWarningFromRegistry != null) {
                    performanceWarningToUse = performanceWarningFromRegistry;
                } else {
                    performanceWarningToUse = this.projectPerformanceWarningThreshold;
                }
                const performanceWarning = Math.ceil(limitCountToUse * (performanceWarningToUse / 100));
                // Disable warning if enabled
                const isPerformanceWarningDisabled = isFeatureFlagEnabled(this._pageContext, Constants.DisablePerformanceWarningFeatureFlag, false);

                const existingProjectNames = existingProjectData.existingProjectNames;
                if (existingProjectNames && existingProjectNames.length > 0) {
                    if (
                        warningCountToUse <= existingProjectNames.length &&
                        (isPerformanceWarningDisabled || existingProjectNames.length < performanceWarning) &&
                        existingProjectNames.length < limitCountToUse
                    ) {
                        // Project count over warning but below limit
                        projectLimitResult.isBelowLimit = true;
                        projectLimitResult.isBelowWarning = false;
                        projectLimitResult.errorReason = format(
                            CreateProjectResources.ProjectLimitErrorMessage_OverWarning,
                            existingProjectNames.length,
                            performanceWarning,
                            limitCountToUse,
                            CreateProjectResources.ProjectLimitDocUrl
                        );
                    } else if (
                        !isPerformanceWarningDisabled &&
                        performanceWarning <= existingProjectNames.length &&
                        existingProjectNames.length < limitCountToUse
                    ) {
                        // Project count over warning but below limit
                        projectLimitResult.isBelowLimit = true;
                        projectLimitResult.isBelowWarning = false;
                        projectLimitResult.errorReason = format(
                            CreateProjectResources.ProjectLimitErrorMessage_OverPerformanceWarning,
                            existingProjectNames.length,
                            limitCountToUse,
                            CreateProjectResources.ProjectLimitDocUrl
                        );
                    } else if (existingProjectNames.length >= limitCountToUse && existingProjectNames.length > warningCountToUse) {
                        // Project count over limit and warning
                        projectLimitResult.isBelowLimit = false;
                        projectLimitResult.isBelowWarning = false;
                        projectLimitResult.errorReason = format(
                            CreateProjectResources.ProjectLimitErrorMessage_OverLimit,
                            existingProjectNames.length,
                            limitCountToUse,
                            CreateProjectResources.ProjectLimitDocUrl
                        );
                    } else {
                        // Project count below limit and warning
                        projectLimitResult.isBelowLimit = true;
                        projectLimitResult.isBelowWarning = true;
                    }
                } else if (!existingProjectNames || existingProjectNames.length == 0) {
                    // There are no existing projects
                    projectLimitResult.isBelowLimit = true;
                    projectLimitResult.isBelowWarning = true;
                }

                if (projectLimitResult && projectLimitResult.isBelowLimit) {
                    // The collection is below limit, invoke the validation succeeded action
                    this._actionsHub.projectLimitCheckSucceeded.invoke({
                        isBelowLimit: projectLimitResult.isBelowLimit,
                        isBelowWarning: projectLimitResult.isBelowWarning,
                        status: {
                            value: StatusValueType.Success,
                            message: projectLimitResult.errorReason
                        }
                    });
                } else {
                    // Show error message only if project name is not empty
                    this._actionsHub.projectLimitCheckFailed.invoke({
                        isBelowLimit: projectLimitResult.isBelowLimit,
                        isBelowWarning: projectLimitResult.isBelowWarning,
                        status: {
                            value: StatusValueType.Failure,
                            message: projectLimitResult.errorReason
                        }
                    });
                }
            }
        });
    }

    /**
     * Two parts of the function
     * 1. Make the CreateProject post api call, which on success internally queues a project creation operation
     * 2. Poll on the project creation operation till we get the result of the operation, either success or failure, is obtained
     * @param newProjectParams - New project creation parameters
     * @param projectNameInTextField - Latest project name in the text field (required to check for validation if changed within delayed validation time)
     * @param existingProjectNames - Dictionary of already existing project names (required for project name validation)
     * @param preCreatedProjectId - If updating the pre-created project, the id of the pre-created project for us to navigate to.
     */
    public createProject(
        newProjectParams: INewProjectParameters,
        projectNameInTextField: string,
        existingProjectNames: { [key: string]: boolean },
        preCreatedProjectId?: string,
        skipRedirectToDetails?: boolean
    ): void {
        if (newProjectParams.projectName !== projectNameInTextField) {
            const validationResult = this._validateProjectName(projectNameInTextField, existingProjectNames);
            if (!validationResult.isValid) {
                return;
            } else {
                newProjectParams.projectName = projectNameInTextField.trim();
            }
        }

        // starting create project initialized scenario.
        const performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const scenarioName = "CreateProjectPanel.CreateProjectInitiated";
        performanceService.startScenario(scenarioName, false);

        const projectName = newProjectParams.projectName;
        // Invoking the project creation started action before actually making the creation call
        this._actionsHub.projectCreationStarted.invoke({
            projectName: projectName,
            navigationUrl: "",
            creationStatus: {
                value: StatusValueType.InProgress,
                message: CreateProjectResources.CreatingNewProjectMessage
            },
            statusMessage: {
                message: CreateProjectResources.CreatingNewProjectMessage,
                statusMessageType: StatusMessageType.Waiting,
                showGoBackCta: false,
                showTryAgainCta: false
            }
        });

        this.apiSource.createProject(newProjectParams).then(
            (projectData: OperationReference) => {
                if (preCreatedProjectId) {
                    // No need to wait on the pre-created project rename. Once the create call returns, it is
                    // safe to navigate to the new project page.
                    performanceService.endScenario(Constants.Area, scenarioName, false);
                    this.handleCreateProjectSuccess(newProjectParams, preCreatedProjectId, skipRedirectToDetails);
                } else {
                    // project creation operation is queued successfully, with operationId projectData.operationId
                    this.operationResultSource.pollOperationResult(projectData.id).then(
                        (operationResult: IOperationResult) => {
                            if (operationResult.isOperationResultSuccess) {
                                performanceService.endScenario(Constants.Area, scenarioName, false);
                                this.handleCreateProjectSuccess(newProjectParams, undefined, skipRedirectToDetails);
                            } else {
                                this._abortPerformanceScenario(performanceService, scenarioName);
                                const errorMessage = this._isHosted
                                    ? CreateProjectResources.CreateProjectOperationFailedErrorText
                                    : CreateProjectResources.CreateProjectOperationFailedErrorTextOnPrem;
                                // The project creation operation failed, hence invoke project creation failed action
                                this._invokeProjectCreationFailedAction(
                                    projectName,
                                    new Error(errorMessage),
                                    operationResult.resultMessage,
                                    this._isHosted ? undefined : operationResult.resultUrl, // TODO(#1339536): Follow up: ResultUrl will start populating download logs link when operations plugin is added.
                                    projectData.id
                                );
                            }
                        },
                        (error: TfsError) => {
                            this._abortPerformanceScenario(performanceService, scenarioName);
                            // The project creation operation result polling failed, hence invoke project creation failed action
                            this._invokeProjectCreationFailedAction(projectName, error);
                        }
                    );
                }
            },
            (error: TfsError) => {
                this._abortPerformanceScenario(performanceService, scenarioName);
                // The project creation api failed and the project creation operation was not queued successfully, hence invoke project creation failed action
                this._invokeProjectCreationFailedAction(projectName, error);
            }
        );
    }

    /**
     * Make force request to data provider and reload the panel
     */
    public reloadPanel = (): void => {
        this.fetchProjectCreationMetadata(true);
        this.fetchExistingProjectNames();
    };

    /**
     * Dismisses the status message overlay
     */
    public dismissStatusMessage = (): void => {
        this._actionsHub.statusMessageDismissed.invoke(undefined);
    };

    public toggleAdvancedOptionsVisibility = (): void => {
        this._actionsHub.advancedOptionVisibilityToggled.invoke(undefined);
    };

    public getProjectLimitFeatureFlag = (): boolean => {
        // Flag was inverted into a kill switch and will revert to the default of on/enabled
        return isFeatureFlagEnabled(this._pageContext, Constants.DisableProjectLimitFeatureFlag, false);
    };

    private handleCreateProjectSuccess(projectParams: INewProjectParameters, preCreatedProjectId?: string, skipRedirectToDetails?: boolean) {
        if (!skipRedirectToDetails) {
            let routeId = Constants.ProjectOverviewRoute;
            if (projectParams.featureIds && projectParams.featureIds.length === 1) {
                const product = this._pageContext
                    .getService<ITfsProductFeaturesService>("ITfsProductFeaturesService")
                    .getProduct(projectParams.featureIds[0]);
                routeId = product ? product.acquisitionRouteId || product.defaultRouteId : routeId;
            }

            // The project creation operation succeeded, hence navigate to the hub of acquired product
            const navigationUrl = routeUrl(this._pageContext, routeId, { project: preCreatedProjectId || projectParams.projectName });

            this._invokeProjectCreationSucceededAction(projectParams.projectName, navigationUrl, CreateProjectResources.CreatingNewProjectMessage); // We continue to show the same CreatingNewProjectMessage while navigating to projectUrl
        }

        this.onCreateProjectComplete && this.onCreateProjectComplete(projectParams.projectName);
    }

    private _abortPerformanceScenario(performanceService: IVssPerformanceService, scenarioName: string): void {
        performanceService.endScenario(Constants.Area, scenarioName, false, undefined, { status: "failed" });
    }

    /**
     * Checks the validity of the new project name and returns validation result with apt reason if invalid.
     * @param newProjectName - Name of the new project
     * @param existingProjectNames - Dictionary with names of the existing projects in the collection
     */
    private _validateProjectName(newProjectName: string, existingProjectNames: { [key: string]: boolean }): ValidationResult {
        let validationResult: ValidationResult = {
            isValid: true,
            invalidNameReason: ""
        };

        if (!newProjectName) {
            validationResult.isValid = false;
            validationResult.invalidNameReason = CreateProjectResources.ProjectNameErrorMessage_EmptyName;
        } else {
            // Validations will be based on the trimmed name, though the displayed name is the actual text input
            const trimmedName = newProjectName.trim();
            validationResult = validateProjectName(trimmedName);
            if (validationResult.isValid) {
                if (existingProjectNames[trimmedName.toLowerCase()]) {
                    // The name is valid and meets requirements but is already used
                    validationResult.isValid = false;
                    validationResult.invalidNameReason = Utils_String.format(CreateProjectResources.CreateProjectNameNotAvailableText, trimmedName);
                }
            }
        }

        return validationResult;
    }

    /**
     * Makes project creation metadata load failed action invocation
     * @param error - Error object
     */
    private _invokeProjectMetadataLoadFailedAction = (error: Error): void => {
        this._actionsHub.projectCreationMetadataLoadFailed.invoke({
            pageStatus: {
                value: StatusValueType.Failure,
                message: error.message
            },
            statusMessage: {
                message: error.message,
                statusMessageType: StatusMessageType.Reload,
                showGoBackCta: false,
                showTryAgainCta: true
            }
        });
    };

    /**
     * Makes the actual project creation failed action invocation with given payload params
     * @param projectName - The given project name
     * @param error - The given error object
     * @param detailedMessage - Optional details for status
     * @param operationLogsLink - Link to the operation logs
     * @param operationId - Optional details for project creation operation id
     */
    private _invokeProjectCreationFailedAction(
        projectName: string,
        error: TfsError,
        detailedMessage?: string,
        operationLogsLink?: string,
        operationId?: string
    ): void {
        // Show "Go Back" CTA if error requires editing form values and re-submit.
        const exceptionsRequiringFormEditing = [
            "ArgumentException",
            "ProjectAlreadyExistsException",
            "InvalidProjectNameException",
            "CannotCreateProjectsWithDisableProcessException",
            "ProjectNameNotRecognizedException"
        ];
        const serverException = error.serverError && error.serverError.typeKey;
        const showGoBackCta = exceptionsRequiringFormEditing.findIndex((exceptionName) => exceptionName === serverException) !== -1;

        this._actionsHub.projectCreationFailed.invoke({
            projectName: projectName,
            navigationUrl: "",
            projectCreationOperationId: operationId,
            creationStatus: {
                value: StatusValueType.Failure,
                message: error.message,
                detailedMessage: detailedMessage
            },
            statusMessage: {
                message: error.message,
                statusMessageType: StatusMessageType.Warning,
                showGoBackCta: showGoBackCta,
                showTryAgainCta: !showGoBackCta,
                operationLogsLink: operationLogsLink
            }
        });
    }

    /**
     * Makes the actual project creation succeeded action invocation with given payload params
     * @param projectName - The given project name
     * @param navigationUrl - Navigation url for the acquired product's hub
     * @param message - The given status message
     */
    private _invokeProjectCreationSucceededAction(projectName: string, navigationUrl: string, message: string): void {
        this._actionsHub.projectCreationSucceeded.invoke({
            projectName: projectName,
            navigationUrl: navigationUrl,
            creationStatus: {
                value: StatusValueType.Success,
                message: message
            },
            statusMessage: {
                message: message,
                statusMessageType: StatusMessageType.Waiting, // We continue to show the waiting experience while navigating to projectUrl
                showGoBackCta: false,
                showTryAgainCta: false
            }
        });
    }
}
